import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Formik, Form, Field, ErrorMessage as FormikErrorMessage } from 'formik';
import * as Yup from 'yup';
import styled from 'styled-components';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import { FiUser, FiMail, FiPhone, FiCalendar, FiUsers, FiInfo, FiHash, FiClock } from 'react-icons/fi';
import { profileService } from '../../services/api';
import { LANGUAGES, languagesForRole } from '../../i18n/languages';
import { useAuth } from '../../hooks/useAuth';

// Styled components for this page
const ProfileContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: ${({ theme }) => theme.spacing[6]};
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ProfileSidebar = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
`;

const ProfilePicture = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 1;
  background-color: ${({ theme }) => theme.colors.backgroundLight};
  border-radius: ${({ theme }) => theme.borders.radius.lg};
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const DefaultAvatar = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.colors.primary}20;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 5rem;
`;

const AvatarUploadButton = styled.label`
  position: absolute;
  bottom: ${({ theme }) => theme.spacing[3]};
  right: ${({ theme }) => theme.spacing[3]};
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.default};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.primaryDark};
  }

  input {
    display: none;
  }
`;

const SidebarSection = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const SidebarTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.md};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing[3]};
`;

const ProfileInfoList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const ProfileInfoItem = styled.li`
  display: flex;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing[3]};
  color: ${({ theme }) => theme.colors.text};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  
  svg {
    margin-right: ${({ theme }) => theme.spacing[2]};
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const ProfileTabs = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
`;

const TabButtons = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[2]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const TabButton = styled.button`
  background: none;
  border: none;
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[4]};
  min-height: 44px;
  cursor: pointer;
  font-size: ${({ theme }) => theme.typography.fontSize.md};
  color: ${({ theme }) => theme.colors.text};
  border-bottom: 2px solid transparent;
  transition: ${({ theme }) => theme.transitions.default};
  
  &.active {
    color: ${({ theme }) => theme.colors.primary};
    border-bottom-color: ${({ theme }) => theme.colors.primary};
  }
  
  &:hover:not(.active) {
    background-color: ${({ theme }) => theme.colors.backgroundLight};
  }
`;

const TabContent = styled.div`
  padding: ${({ theme }) => theme.spacing[1]};
`;

const FormGroup = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const Label = styled.label`
  display: block;
  margin-bottom: ${({ theme }) => theme.spacing[2]};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
`;

const Input = styled(Field)`
  width: 100%;
  padding: ${({ theme }) => theme.spacing[3]};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.base}; /* >=16px prevents iOS zoom on focus */
  transition: ${({ theme }) => theme.transitions.default};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary}20;
  }

  &:disabled {
    background-color: ${({ theme }) => theme.colors.backgroundLight};
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    min-height: 44px;
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing[4]};
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ErrorMessage = styled.div`
  color: ${({ theme }) => theme.colors.error};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  margin-top: ${({ theme }) => theme.spacing[1]};
`;

const FormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing[3]};
  margin-top: ${({ theme }) => theme.spacing[6]};

  @media (max-width: 576px) {
    & > button {
      width: 100%;
      min-height: 48px;
    }
  }
`;

const AlertMessage = styled.div`
  padding: ${({ theme }) => theme.spacing[3]};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  
  ${({ type, theme }) => {
    if (type === 'success') {
      return `
        background-color: ${theme.colors.success}15;
        color: ${theme.colors.success};
      `;
    } else if (type === 'error') {
      return `
        background-color: ${theme.colors.error}15;
        color: ${theme.colors.error};
      `;
    }
    return '';
  }}
`;

// Form validation schemas
const buildProfileSchema = (t) => Yup.object().shape({
  firstName: Yup.string().required(t('employee.profile.validation.firstNameRequired')),
  lastName: Yup.string().required(t('employee.profile.validation.lastNameRequired')),
  email: Yup.string().email(t('employee.profile.validation.emailInvalid')).required(t('employee.profile.validation.emailRequired')),
  phone: Yup.string().matches(/^\+?[0-9()\-.\s]{6,20}$/, { message: t('employee.profile.validation.phoneInvalid'), excludeEmptyString: true }),
});

// The backend serves /uploads/* on the API host (without the /api suffix)
const API_ORIGIN = (process.env.REACT_APP_API_URL || 'http://localhost:3001/api').replace(/\/api$/, '');

const resolveAvatarUrl = (url) => {
  if (!url) return null;
  return url.startsWith('/') ? `${API_ORIGIN}${url}` : url;
};

const formatDate = (value) => {
  if (!value) return '—';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '—' : parsed.toLocaleDateString();
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '—' : parsed.toLocaleString();
};

const buildPasswordSchema = (t) => Yup.object().shape({
  currentPassword: Yup.string().required(t('employee.profile.validation.currentPasswordRequired')),
  newPassword: Yup.string()
    .min(8, t('employee.profile.validation.passwordTooShort'))
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+])[A-Za-z\d!@#$%^&*()_+]{8,}$/,
      t('employee.profile.validation.passwordComplexity')
    )
    .required(t('employee.profile.validation.newPasswordRequired')),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('newPassword'), null], t('employee.profile.validation.passwordsMustMatch'))
    .required(t('employee.profile.validation.confirmPasswordRequired')),
});

const Profile = () => {
  const { currentUser } = useAuth();
  const passwordAccount = ['admin', 'support'].includes(currentUser?.userType);
  const { t, i18n } = useTranslation();
  const profileSchema = useMemo(() => buildProfileSchema(t), [t]);
  const passwordSchema = useMemo(() => buildPasswordSchema(t), [t]);
  const [activeTab, setActiveTab] = useState('personal');
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [alert, setAlert] = useState(null);

  const fetchProfile = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const profileData = await profileService.getProfile();
      setProfile(profileData);
      setAvatarUrl(profileData?.avatarUrl || null);
    } catch (error) {
      console.error('Error fetching profile:', error);
      setLoadError(t('employee.profile.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  // Change the preferred UI language: apply it immediately AND persist it so it
  // becomes the default on next login and is visible to the admin (REQ #5).
  const handleLanguageChange = async (newLang) => {
    try {
      if (newLang) i18n.changeLanguage(newLang);
      const updated = await profileService.updateProfile({ language: newLang || null });
      setProfile(updated);
      setAlert({ type: 'success', message: t('employee.profile.languageSaved', { defaultValue: 'Language updated' }) });
    } catch (error) {
      setAlert({ type: 'error', message: error.message || t('employee.profile.updateError') });
    }
  };

  const handleProfileUpdate = async (values, { setSubmitting }) => {
    try {
      setAlert(null);
      const payload = {
        firstName: values.firstName,
        lastName: values.lastName,
        phone: values.phone,
        position: values.position,
        department: values.department,
      };
      const updatedProfile = await profileService.updateProfile(payload);
      setProfile(updatedProfile);
      setAlert({
        type: 'success',
        message: t('employee.profile.updateSuccess'),
      });
    } catch (error) {
      console.error('Error updating profile:', error);
      setAlert({
        type: 'error',
        message: error.message || t('employee.profile.updateError'),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handlePasswordChange = async (values, { setSubmitting, resetForm }) => {
    try {
      setAlert(null);
      await profileService.changePassword(values.currentPassword, values.newPassword);
      setAlert({
        type: 'success',
        message: t('employee.profile.passwordChangeSuccess'),
      });
      resetForm();
    } catch (error) {
      console.error('Error changing password:', error);
      setAlert({
        type: 'error',
        message: error.message || t('employee.profile.passwordChangeError'),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      setAlert({
        type: 'error',
        message: t('employee.profile.avatarInvalidType'),
      });
      return;
    }

    try {
      setAvatarUploading(true);
      setAlert(null);
      const result = await profileService.updateAvatar(file);
      setAvatarUrl(result.avatarUrl);
      setAlert({
        type: 'success',
        message: t('employee.profile.avatarSuccess'),
      });
    } catch (error) {
      console.error('Error uploading avatar:', error);
      setAlert({
        type: 'error',
        message: error.message || t('employee.profile.avatarError'),
      });
    } finally {
      setAvatarUploading(false);
    }
  };

  if (loading) {
    return (
      <div>
        <h1>{t('employee.profile.title')}</h1>
        <Card padding="md">{t('employee.profile.loading')}</Card>
      </div>
    );
  }

  // A failed load must not crash the page rendering a null profile
  if (!profile) {
    return (
      <div>
        <h1>{t('employee.profile.title')}</h1>
        <AlertMessage type="error">
          {loadError || t('employee.profile.loadError')}
        </AlertMessage>
        <Button onClick={fetchProfile}>{t('common.retry')}</Button>
      </div>
    );
  }

  return (
    <div>
      <h1>{t('employee.profile.title')}</h1>

      {alert && (
        <AlertMessage type={alert.type}>
          {alert.message}
        </AlertMessage>
      )}

      <ProfileContainer>
        <ProfileSidebar>
          <Card padding="md">
            <ProfilePicture>
              {avatarUrl ? (
                <img src={resolveAvatarUrl(avatarUrl)} alt={t('employee.profile.avatarAlt')} />
              ) : (
                <DefaultAvatar>
                  <FiUser />
                </DefaultAvatar>
              )}
              <AvatarUploadButton>
                <input 
                  type="file" 
                  accept="image/*" 
                  onChange={handleAvatarChange}
                  disabled={avatarUploading}
                />
                {avatarUploading ? '⌛' : '📷'}
              </AvatarUploadButton>
            </ProfilePicture>
            
            <SidebarSection>
              <SidebarTitle>{t('employee.profile.sidebar.profileInfo')}</SidebarTitle>
              <ProfileInfoList>
                <ProfileInfoItem>
                  <FiUser />
                  <strong>{profile.firstName} {profile.lastName}</strong>
                </ProfileInfoItem>
                <ProfileInfoItem>
                  <FiMail />
                  {profile.email}
                </ProfileInfoItem>
                <ProfileInfoItem>
                  <FiPhone />
                  {profile.phone || t('employee.profile.noPhone')}
                </ProfileInfoItem>
              </ProfileInfoList>
            </SidebarSection>

            <SidebarSection>
              <SidebarTitle>{t('employee.profile.sidebar.additionalInfo')}</SidebarTitle>
              <ProfileInfoList>
                <ProfileInfoItem>
                  <FiUsers />
                  {profile.department || t('employee.profile.noDepartment')}
                </ProfileInfoItem>
                <ProfileInfoItem>
                  <FiInfo />
                  {profile.position || t('employee.profile.noPosition')}
                </ProfileInfoItem>
                <ProfileInfoItem>
                  <FiHash />
                  {profile.employeeCode || '—'}
                </ProfileInfoItem>
                <ProfileInfoItem>
                  <FiCalendar />
                  {t('employee.profile.joined', { date: formatDate(profile.joinDate) })}
                </ProfileInfoItem>
                <ProfileInfoItem>
                  <FiClock />
                  {t('employee.profile.lastLogin', { date: formatDateTime(profile.lastLogin) })}
                </ProfileInfoItem>
              </ProfileInfoList>
            </SidebarSection>
          </Card>
        </ProfileSidebar>
        
        <ProfileTabs>
          <Card padding="md">
            <TabButtons>
              <TabButton
                className={activeTab === 'personal' ? 'active' : ''}
                onClick={() => setActiveTab('personal')}
              >
                {t('employee.profile.tab.personal')}
              </TabButton>
              <TabButton
                className={activeTab === 'security' ? 'active' : ''}
                onClick={() => setActiveTab('security')}
              >
                {t('employee.profile.tab.security')}
              </TabButton>
            </TabButtons>
            
            <TabContent>
              {activeTab === 'personal' && (
                <>
                <FormGroup style={{ maxWidth: 340 }}>
                  <Label htmlFor="prefLanguage">{t('employee.profile.language', { defaultValue: 'Preferred language' })}</Label>
                  <select
                    id="prefLanguage"
                    value={profile.language || ''}
                    onChange={(e) => handleLanguageChange(e.target.value)}
                    style={{ width: '100%', padding: '0.6rem 0.7rem', border: '1px solid var(--border-subtle)', borderRadius: '0.375rem', fontSize: '0.95rem', background: '#fff' }}
                  >
                    <option value="">{t('employee.profile.languageDefault', { defaultValue: 'Browser default' })}</option>
                    {languagesForRole('employee').map((code) => (
                      <option key={code} value={code}>{LANGUAGES[code] ? LANGUAGES[code].nativeName : code}</option>
                    ))}
                  </select>
                </FormGroup>
                <Formik
                  initialValues={{
                    firstName: profile.firstName || '',
                    lastName: profile.lastName || '',
                    email: profile.email || '',
                    phone: profile.phone || '',
                    position: profile.position || '',
                    department: profile.department || '',
                  }}
                  validationSchema={profileSchema}
                  onSubmit={handleProfileUpdate}
                >
                  {({ isSubmitting }) => (
                    <Form>
                      <FormRow>
                        <FormGroup>
                          <Label htmlFor="firstName">{t('employee.profile.firstName')}</Label>
                          <Input type="text" id="firstName" name="firstName" />
                          <FormikErrorMessage name="firstName" component={ErrorMessage} />
                        </FormGroup>

                        <FormGroup>
                          <Label htmlFor="lastName">{t('employee.profile.lastName')}</Label>
                          <Input type="text" id="lastName" name="lastName" />
                          <FormikErrorMessage name="lastName" component={ErrorMessage} />
                        </FormGroup>
                      </FormRow>

                      <FormGroup>
                        <Label htmlFor="email">{t('employee.profile.emailAddress')}</Label>
                        <Input type="email" id="email" name="email" disabled />
                        <FormikErrorMessage name="email" component={ErrorMessage} />
                      </FormGroup>

                      <FormGroup>
                        <Label htmlFor="phone">{t('employee.profile.phoneNumber')}</Label>
                        <Input type="tel" id="phone" name="phone" />
                        <FormikErrorMessage name="phone" component={ErrorMessage} />
                      </FormGroup>

                      <FormRow>
                        <FormGroup>
                          <Label htmlFor="position">{t('employee.profile.position')}</Label>
                          <Input type="text" id="position" name="position" />
                        </FormGroup>

                        <FormGroup>
                          <Label htmlFor="department">{t('employee.profile.department')}</Label>
                          <Input type="text" id="department" name="department" />
                        </FormGroup>
                      </FormRow>

                      <FormActions>
                        <Button
                          type="submit"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? t('common.saving') : t('employee.profile.saveChanges')}
                        </Button>
                      </FormActions>
                    </Form>
                  )}
                </Formik>
                </>
              )}

              {activeTab === 'security' && !passwordAccount && <p>{t('auth.access.profileLinkOnly')}</p>}
              {activeTab === 'security' && passwordAccount && (
                <Formik
                  initialValues={{
                    currentPassword: '',
                    newPassword: '',
                    confirmPassword: '',
                  }}
                  validationSchema={passwordSchema}
                  onSubmit={handlePasswordChange}
                >
                  {({ isSubmitting }) => (
                    <Form>
                      <FormGroup>
                        <Label htmlFor="currentPassword">{t('employee.profile.currentPassword')}</Label>
                        <Input
                          type="password"
                          id="currentPassword"
                          name="currentPassword"
                          autoComplete="current-password"
                        />
                        <FormikErrorMessage name="currentPassword" component={ErrorMessage} />
                      </FormGroup>

                      <FormGroup>
                        <Label htmlFor="newPassword">{t('employee.profile.newPassword')}</Label>
                        <Input
                          type="password"
                          id="newPassword"
                          name="newPassword"
                          autoComplete="new-password"
                        />
                        <FormikErrorMessage name="newPassword" component={ErrorMessage} />
                      </FormGroup>

                      <FormGroup>
                        <Label htmlFor="confirmPassword">{t('employee.profile.confirmNewPassword')}</Label>
                        <Input
                          type="password"
                          id="confirmPassword"
                          name="confirmPassword"
                          autoComplete="new-password"
                        />
                        <FormikErrorMessage name="confirmPassword" component={ErrorMessage} />
                      </FormGroup>

                      <FormActions>
                        <Button
                          type="submit"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? t('employee.profile.changing') : t('employee.profile.changePassword')}
                        </Button>
                      </FormActions>
                    </Form>
                  )}
                </Formik>
              )}
            </TabContent>
          </Card>
        </ProfileTabs>
      </ProfileContainer>
    </div>
  );
};

export default Profile;
